import { useState, useEffect } from 'react'
import { useUserApi } from "hooks/use-user-api/useUserApi"
import { useAuth } from "pages/auth/hooks/useAuth"
import { useHistory, useLocation } from 'react-router-dom'
import { OnboardingQuestion, PartnerType, QuestionAnswer, UserRoles, BackendPartner, SubscribedPartner } from 'common/types'
import { NotificationEnum, useNotification } from 'hooks/use-notification/useNotification'
import { useOnboardingQuestionsApi } from 'hooks/use-onboarding-questions-api/useOnboardingQuestionsApi'
import { useAnalytics, AnalyticUserType } from 'hooks/use-analytics/useAnalytics'
import { Connection } from 'common/types/connection.types'
import { useConnectionApi } from 'hooks/use-connection-api/useConnectionApi'
import { useLocalStorage } from 'hooks/use-local-storage/useLocalStorage'
import { usePartnerApi } from 'hooks/use-partner-api/usePartnerApi'
import { useSubscriptionApi } from 'hooks/use-subscription-api/useSubscriptionApi'
import { useSubscriptionPromoCode } from './useSubscriptionPromoCode'

export const useOnboarding = () => {
    const { backendUser, backendPartner, setUser, forwardingUrl, setForwardingUrl, setPartner } = useAuth()
    const [showOnboarding, setShowOnboarding] = useState(true)
    const history = useHistory()
    const location = useLocation()
    const { notify } = useNotification()
    const { createPaidPartnerProfileWithApiKey, createPartnerProfileWithApiKey } = usePartnerApi()
    const { onboardUser, onboardPartner, invite, fetchUser } = useUserApi()
    const { fetchAllOnboardingQuestionsByRole } = useOnboardingQuestionsApi()
    const [questionAnswers, setQuestionAnswers] = useState<QuestionAnswer[]>()
    const [onboardingQuestions, setOnboardingQuestions] = useState<OnboardingQuestion[]>()
    const [partnerOnboardingQuestions, setPartnerOnboardingQuestions] = useState<OnboardingQuestion[]>()
    const { createInstantConnection } = useConnectionApi()
    const { logEventAnalytics, handleTracker } = useAnalytics()
    const { clearItem, getItem } = useLocalStorage()
    const [showPartner, setShowPartner] = useState(location.pathname?.includes('partner') || location.pathname?.includes('company') ? true : false)
    const [subscriptionType, setSubscriptionType] = useState<string>()
    const { fetchSubscriptionByName, fetchAllSubscriptions } = useSubscriptionApi()
    const { getPartnerProfileByName, fetchPartner } = usePartnerApi()
    const {
      verifySubscriptionPromoCode,
      createPartnerByPromoCode,
      promoCode,
      setPromoCode,
      codeIsVerified,
      setCodeIsVerified
    } = useSubscriptionPromoCode()

    const getAnswer = (question) => {
        switch (question) {
            case 'First Name':
                return backendUser.firstName

            case 'Last Name':
                return backendUser.lastName

            case 'Company Email Address':
                return backendPartner?.email
            default:
                return ''
        }
    }

    const completeOnboarding = async (values: any[], partnerValues?: boolean) => {
        const partner = JSON.parse(getItem('partnerconfig'))
        const companyName = values?.find(value => value?.question === 'Company Name')?.answer

        if (partnerValues && backendUser) {
            let partnerType = PartnerType.Community
            if (partner?.packageType)
                partnerType = PartnerType.Corporate

            const newPartner: BackendPartner = {
                email: backendUser.username,
                type: partnerType,
                name: companyName
            }
            let partnerResponse: SubscribedPartner
            if (codeIsVerified) {
                partnerResponse = await createPartnerByPromoCode(backendUser?.id, promoCode, newPartner)
            } else {
                partnerResponse =
                partnerType === PartnerType.Community
                  ? await createPartnerProfileWithApiKey(backendUser?.id, newPartner)
                  : await createPaidPartnerProfileWithApiKey(
                      backendUser?.id,
                      subscriptionType || partner?.packageType.toLowerCase(),
                      newPartner
                    )
            }
            const partnerOnboardResponse = await onboardPartner(partnerResponse?.partner?.id, values)
            setPartner(partnerOnboardResponse)
            setUser(partnerResponse?.user)

            history.push(forwardingUrl ? forwardingUrl?.url : '/feed')
            setForwardingUrl(null)
            clearItem('partnerconfig')
            setShowPartner(false)
            logEventAnalytics(
              'Partner',
              'Account created',
              'mixpanel',
              'Partner created an account',
              {
                'Account Type': 'Partner',
                'Partner Type': partnerType,
                'User ID': backendUser?.id,
                'Partner ID': partnerResponse?.partner?.id,
                Plan: subscriptionType || partner?.packageType?.toLowerCase(),
                'Company Name': partnerResponse?.partner?.name
              },
              backendUser?.id,
              { $name: `${backendUser?.firstName} ${backendUser?.lastName}`, $email: backendUser?.username }
            )
            return
        }

        const avatarQuestionAnswer = values?.find(q => q?.question === 'avatar')
        if (avatarQuestionAnswer && !avatarQuestionAnswer.answer && backendUser?.image && backendUser.authType === 'sso') {
            avatarQuestionAnswer.answer = backendUser?.image
            values[values.indexOf(avatarQuestionAnswer)] = avatarQuestionAnswer
        }
        const response = await onboardUser(backendUser.id, values)

        if (response) {
            setUser({ ...backendUser, ...response })
            if (partner && (typeof partner.packageType == 'string' || partner.community === 'true')) {
                history.push('/settings/partner')
                history.go(0)
                return
            }
                const invitePeepUrl = JSON.parse(getItem('url'))
                const userId =
                  invitePeepUrl?.url?.split('/').length > 2
                    ? invitePeepUrl?.url?.split('/')[2].substring(0, invitePeepUrl?.url?.split('/')[2].indexOf('?'))
                    : undefined
                const inviteFollowerUrl = invitePeepUrl
                const companyId = inviteFollowerUrl?.url?.split('/').length > 2
                    ? invitePeepUrl?.url?.split('/')[2].substring(0, invitePeepUrl?.url?.split('/')[2].indexOf('?'))
                    : undefined

                if(inviteFollowerUrl?.url?.includes('follow')){
                      const partner = await fetchPartner(companyId)
                      const logData = {
                        'Referrer email': partner?.email,
                        'Invite accepted from': companyId,
                        'User ID': backendUser?.id,
                        'Account Type': partner?.id,
                        'Company Name': partner?.name,
                        'Partner ID': partner?.id
                      }
                    await handleTracker(AnalyticUserType.ALL, 'Follower Invite accepted', logData, 'mixpanel', null, backendUser)
                }
                    

                if (invitePeepUrl?.url?.includes('connect') && userId) {
                    const user = await fetchUser(userId)
                    const newConnection: Connection = {
                        userId: backendUser?.id,
                        connectId: userId,
                        status: 'accepted',
                        firstName: user?.firstName,
                        lastName: user?.lastName,
                        title: user?.onboardingQuestions?.filter(x => x?.question === 'Headline')[0]?.answer
                }
                    await createInstantConnection(userId, backendUser?.id, newConnection)
                    const logData = {
                        'Referrer email': user?.username,
                        'Invite accepted from': userId,
                        'User ID': backendUser?.id,
                        'Account Type': backendUser?.partnerId ? 'Partner' : backendUser?.role,
                        'Company Name': backendPartner?.name,
                        'Partner ID': backendPartner?.id
                    }
                    await handleTracker(AnalyticUserType.ALL, 'Invite accepted', logData, 'mixpanel', null, backendUser)
                    window.location.reload()
                }
                // handle new user onboarding from summit page
                if (forwardingUrl && forwardingUrl.url?.includes('summit')) {
                    history.push(forwardingUrl.url, 'newUser')
                } else {
                history.push(forwardingUrl ? forwardingUrl?.url : '/feed')
                }
                setForwardingUrl(null)
            }
    }

    const completeEditOnboarding = async (values: any[], redirect: boolean=true) => {
        if (backendUser?.partnerId && showPartner) {
            const partnerResponse = await onboardPartner(backendUser?.partnerId, values)
            setPartner({...backendPartner, ...partnerResponse })
            notify(NotificationEnum.SUCCESS, 'Updated successfully!')
            if(redirect){
                history.push(forwardingUrl ? forwardingUrl?.url : location.pathname, { ...backendUser, ...partnerResponse })
                setForwardingUrl(null)
            }
            return
        }

        const response = await onboardUser(backendUser.id, values)
        if (response) {
            notify(NotificationEnum.SUCCESS, 'Updated successfully!')
            setUser({ ...backendUser, ...response })
            if(redirect){
                history.push(forwardingUrl ? forwardingUrl?.url : location.pathname, { ...backendUser, ...response })
                setForwardingUrl(null)
            }
            return response
        }
    }

    const inviteUsersByEmail = async (emails: string[]) => {
        const response = await invite(backendUser.id, emails)
        if (response) {
            emails?.forEach(x => notify('success', `Invitation successfully sent to ${x}`))
            logEventAnalytics(
                'Member',
                `Contact invited`,
                'both',
                'Sent invite',
                { Email: backendUser?.username, 'Invited by': 'Email' },
                backendUser?.id,
                { $name: `${backendUser?.firstName} ${backendUser?.lastName}`, $email: backendUser?.username }
            )
        }
    }

    const isDuplicateCompanyName = async (companyName: string) => {
        const existingCompanyName: string = await getPartnerProfileByName(companyName)
        return existingCompanyName?.length ? true : false
    }

    const onSubmitPayment = () => {

    }

    useEffect(() => {
        const getData = async () => {
            if (backendUser && backendUser?.role && !onboardingQuestions?.length ) {
                const response: OnboardingQuestion[] = await fetchAllOnboardingQuestionsByRole(showPartner ? UserRoles.Partner : backendUser?.role)
                setOnboardingQuestions([
                    ...response,
                    { question: 'emails', role: backendUser?.role, enabled: true, required: false, page: 6, pageOrder: 0, displayType: 'email' }
                ])
                const questions = response?.map(question => {
                    return { question: question?.question, answer: getAnswer(question?.question) } as QuestionAnswer
                })
                setQuestionAnswers(questions)
            }
            if (backendUser && backendUser?.partnerId && !partnerOnboardingQuestions?.length) {
              const response: OnboardingQuestion[] = await fetchAllOnboardingQuestionsByRole(UserRoles.Partner)
              setPartnerOnboardingQuestions(response)
            }
        }
        getData()
        
    }, [backendUser, backendPartner, showPartner])
    

    return {
        onboardingUser: backendUser,
        setOnboardingUser: setUser,
        completeOnboarding,
        completeEditOnboarding,
        showOnboarding,
        setShowOnboarding,
        onboardingQuestions,
        partnerOnboardingQuestions,
        questionAnswers,
        setQuestionAnswers,
        inviteUsersByEmail,
        onSubmitPayment,
        showPartner,
        setSubscriptionType,
        fetchSubscriptionByName,
        fetchAllSubscriptions,
        isDuplicateCompanyName,
        verifySubscriptionPromoCode,
        promoCode,
        setPromoCode,
        codeIsVerified,
        setCodeIsVerified
    } as const
}